import { useMemo, useState, useEffect } from 'react'
import styles from './style.module.scss'
import { TApi } from '../../../../shared/const'
import http from '../../../../shared/http'
import { documentOwnerName, getFormattedDate, localizedDocumentTypeLabel } from '../../../../shared/helpers'
import MiniHeader from '../../../../UI/MiniHeader'
import Table from '../../../../UI/Table'
import FilePreview from '../../../../UI/FilePreview'
import Btn from '../../../../UI/Btn'
import invoiceIcon from '../../../../assets/icons/invoices.svg'
import reportIcon from '../../../../assets/icons/reportIcon.svg'
import businessPlanIcon from '../../../../assets/icons/businessPlan.svg'
import insuranceIcon from '../../../../assets/icons/insuranceIcon.svg'
import paperParentalIcon from '../../../../assets/icons/paperParental.svg'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { maybeShowSupporterInvitation } from '../../../../shared/supporterInvitation'

const documentTypesIcon = {
	INVOICE: invoiceIcon,
	REPORT: reportIcon,
	BUSINESS_PLAN: businessPlanIcon,
	INSURANCE: insuranceIcon,
	PARENTAL_CONFIRMATION: paperParentalIcon,
	CONSENT_DOCUMENT: businessPlanIcon,
	PARTICIPATION_DOCUMENT: businessPlanIcon,
	COMPANY_CERTIFICATE: businessPlanIcon,
	SHAREHOLDER_CERTIFICATE: businessPlanIcon,
	SHARE_TEMPLATE: businessPlanIcon,
	COURSE_CERTIFICATE: paperParentalIcon
}

export default function DocumentsList() {
	const { t } = useTranslation()
	const [documents, setDocuments] = useState([])
	const [countAll, setCountAll] = useState(0)
	const [page, setPage] = useState(0)
	const [sort, setSort] = useState({ name: 'name', direction: 'asc' })
	// Position der Datei in der Vorschau; -1 heisst: keine Vorschau offen.
	const [previewIndex, setPreviewIndex] = useState(-1)
	const limit = 10
	const role = useSelector(state => state.user.role)
	const companyType = useSelector(state => state.user.profile?.EmploymentCompany?.companyType)

	const fetchDocuments = offset => {
		http.get(TApi.STUDENT_GET_DOCUMENTS, {
			params: {
				limit,
				offset,
				order: sort.name,
				direction: sort.direction
			}
		}).then(({ data }) => {
			// Neue Liste, alte Position: Die Vorschau zeigt sonst die Datei, die
			// an dieser Stelle vorher stand.
			setPreviewIndex(-1)
			setDocuments(data.documents.rows)
			setCountAll(data.documents.count)
		})
	}
	const handleSort = (name, direction) => {
		setSort({ name, direction })
	}
	// `type` ist die Art des Dokuments: Nach der Teilnahmeurkunde kommt für
	// JC/JCC der Hinweis auf die Unterstützer:innen-Registrierung (#212) —
	// erst nachdem der Download angestoßen ist, er wird nicht aufgehalten.
	const downoload = (name, type) => {
		http.get(TApi.STUDENT_DOWNLOAD_DOCUMENT(name), {
			headers: {
				'Content-Type': 'application/pdf'
			},
			responseType: 'blob'
		}).then(({ data }) => {
			const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }))
			const link = document.createElement('a')
			link.href = url
			link.setAttribute('download', name)
			document.body.appendChild(link)
			link.click()
			link.parentNode.removeChild(link)
			maybeShowSupporterInvitation({ role, companyType, documentType: type })
		})
	}

	useEffect(() => {
		fetchDocuments(page * limit)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page, sort])

	// Geblättert wird durch die Liste, die gerade zu sehen ist — dieselbe
	// Reihenfolge, dieselbe Seite.
	const previewItems = useMemo(
		() =>
			documents.map(item => ({
				name: item.originalName,
				url: TApi.STUDENT_DOWNLOAD_DOCUMENT(item.name),
				file: item.name,
				type: item.type,
				meta: [localizedDocumentTypeLabel(item.type), getFormattedDate(item.createdAt)]
					.filter(Boolean)
					.join(' · ')
			})),
		[documents]
	)

	const columns = useMemo(
		() => [
			{
				Header: () => {
					return <span className={styles.tableTitleDocumentName}>{t('Document name')}</span>
				},
				minWidth: 250,
				accessor: 'name',
				Cell: ({ row }) => {
					return (
						<div className={styles.tableNameContainer}>
							<img
								className={styles.documentIcon}
								src={documentTypesIcon[row.original.type]}
								alt="icon"
							/>
							<span className={styles.documentName}>{row.original.originalName}</span>
						</div>
					)
				}
			},
			{
				Header: t('Type'),
				minWidth: 250,
				accessor: 'type',
				Cell: ({ row }) => {
					return (
						<p className={styles.type}> {localizedDocumentTypeLabel(row.original.type)}</p>
					)
				}
			},
			/*
			 * Wem das Dokument gehoert. Erzeugte Unterlagen — Urkunden,
			 * Firmenzertifikate — haben niemanden; frueher lief die Zeile
			 * dafuer auf einen Fehler, weil `Creator` dann fehlt.
			 */
			{
				Header: t('Assigned to'),
				minWidth: 200,
				accessor: 'employee',
				Cell: ({ row }) => {
					const owner = documentOwnerName(row.original.Creator)

					return <p className={styles.employee}>{owner || <span aria-hidden="true">—</span>}</p>
				}
			},
			{
				Header: t('Date'),
				accessor: 'time',
				Cell: ({ row }) => {
					return (
						<div className={styles.dateContainer}>
							<p className={styles.date}>{getFormattedDate(row.original.createdAt)}</p>
							<Btn
								theme="download"
								icon="download"
								iconSize={{ w: 24, h: 24 }}
								onClick={() => downoload(row.original.name, row.original.type)}
							/>
						</div>
					)
				}
			}
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[documents, t, role, companyType]
	)
	return (
		<div className={styles.root}>
			<MiniHeader title={t('Company Documents')} bg="#f8faff" />
			<div className="container">
				<Table
					columns={columns}
					items={documents}
					page={page}
					limit={limit}
					countAll={countAll}
					onChange={setPage}
					handleSort={handleSort}
					onRowClick={(item, row) => setPreviewIndex(row.index)}
					rowTitle={t('Preview')}
				/>
			</div>
			{previewIndex >= 0 && (
				<FilePreview
					items={previewItems}
					index={previewIndex}
					onIndexChange={setPreviewIndex}
					onClose={() => setPreviewIndex(-1)}
					onDownload={item => downoload(item.file, item.type)}
				/>
			)}
		</div>
	)
}
